// Pure nav-packing logic for the AppBar: which nav items render inline and
// which fold into the "More" overflow dropdown. Split from app-bar.tsx so the
// packing rules are unit-testable without rendering.

export const NAV_GAP_PX = 12; // matches Tailwind gap-3 on the inline nav row

export type AppBarNavLink = {
  kind?: "link";
  label: string;
  href: string;
  // Path prefix used to highlight the item across sub-routes. Defaults to
  // `href`. Set when the link points to a default sub-page (e.g.
  // `/funder/understand/overview`) but the tab should stay active across
  // a broader section (any `/funder/...` route).
  activePath?: string;
  badgeCount?: number;
  disabled?: boolean;
  // Soft "phase-irrelevant but accessible" treatment — 60% opacity, still
  // clickable. Mutually exclusive with `disabled`.
  dimmed?: boolean;
  tooltip?: string;
  // Show an "unseen" dot indicator on the item. Consumer decides when to
  // surface it (typically active-and-not-yet-visited).
  unseen?: boolean;
  // Fired when the item is clicked/activated — used by consumers to mark
  // the item as visited.
  onActivate?: () => void;
  // Start this item in the "More" overflow regardless of available width
  // (role-based folding, e.g. de-emphasized tabs for a given user). The
  // active item still renders inline, and width pressure can fold further
  // items on top — both merge into the same single More menu.
  defaultFolded?: boolean;
};

export type AppBarNavDropdownItem = {
  label: string;
  href: string;
  badgeCount?: number;
  disabled?: boolean;
  dimmed?: boolean;
  tooltip?: string;
  unseen?: boolean;
  onActivate?: () => void;
};

export type AppBarNavDropdown = {
  kind: "dropdown";
  label: string;
  activePath?: string;
  items: AppBarNavDropdownItem[];
  // Aggregate "any unseen child" indicator on the dropdown trigger — mirrors
  // SetupNavDropdown's parent-button dot. The dropdown items each own their
  // own per-leaf dot via AppBarNavDropdownItem.unseen.
  unseen?: boolean;
  // See AppBarNavLink.defaultFolded. A folded dropdown renders as a nested
  // submenu inside More.
  defaultFolded?: boolean;
};

export type AppBarNavItem = AppBarNavLink | AppBarNavDropdown;

export function navItemKey(item: AppBarNavItem): string {
  return item.kind === "dropdown" ? `dropdown:${item.label}` : item.href;
}

export function matchesPath(pathname: string, target: string): boolean {
  return pathname === target || pathname.startsWith(`${target}/`);
}

export function isItemActive(pathname: string, item: AppBarNavItem): boolean {
  if (item.kind === "dropdown") {
    if (item.activePath && matchesPath(pathname, item.activePath)) {
      return true;
    }

    return item.items.some((sub) => matchesPath(pathname, sub.href));
  }

  return matchesPath(pathname, item.activePath ?? item.href);
}

// Any unseen indicator among folded items, surfaced on the More trigger so
// role-folded tabs don't lose their dot inside the closed menu.
export function foldedUnseen(folded: AppBarNavItem[]): boolean {
  return folded.some((item) =>
    item.kind === "dropdown"
      ? item.unseen || item.items.some((sub) => sub.unseen)
      : item.unseen,
  );
}

export type PackResult = {
  inline: AppBarNavItem[];
  folded: AppBarNavItem[];
  // Minimum space the inline cluster needs to render `active + More` when
  // collision is active. Wired into the cluster's `min-width` so the title
  // cluster shrinks/truncates instead of pushing the nav off-screen.
  // **Stays at 0 in the "everything fits" case** — otherwise min-width
  // would clamp `container.clientWidth`, the ResizeObserver would never
  // see a smaller width, and items would never fold when the viewport
  // shrinks (e.g. iPad rotating from landscape to portrait).
  minInlineWidth: number;
};

/**
 * Content equality for pack results. Callers rebuild `navLinks` freely, so
 * identity comparison would never match; instead compare everything the
 * AppBar renders FROM the pack result — item structure and display flags —
 * so bailing to the previous result can never pin stale UI (a changed unseen
 * dot or disabled flag makes the results unequal). `onActivate` handlers are
 * deliberately ignored: they are rebuilt-per-render closures over the same
 * behavior. Used by the AppBar's collision hook to bail out of no-op
 * setState calls (SP-2649).
 */
export function packResultsEqual(a: PackResult, b: PackResult): boolean {
  return (
    a.minInlineWidth === b.minInlineWidth &&
    navListsEqual(a.inline, b.inline) &&
    navListsEqual(a.folded, b.folded)
  );
}

function navListsEqual(a: AppBarNavItem[], b: AppBarNavItem[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (!navItemsEqual(a[i]!, b[i]!)) {
      return false;
    }
  }

  return true;
}

function navItemsEqual(a: AppBarNavItem, b: AppBarNavItem): boolean {
  if (a.kind === "dropdown" || b.kind === "dropdown") {
    if (a.kind !== "dropdown" || b.kind !== "dropdown") {
      return false;
    }

    if (
      a.label !== b.label ||
      a.activePath !== b.activePath ||
      a.unseen !== b.unseen ||
      a.defaultFolded !== b.defaultFolded ||
      a.items.length !== b.items.length
    ) {
      return false;
    }

    for (let i = 0; i < a.items.length; i++) {
      if (!dropdownItemsEqual(a.items[i]!, b.items[i]!)) {
        return false;
      }
    }

    return true;
  }

  return (
    a.label === b.label &&
    a.href === b.href &&
    a.activePath === b.activePath &&
    a.disabled === b.disabled &&
    a.dimmed === b.dimmed &&
    a.tooltip === b.tooltip &&
    a.unseen === b.unseen &&
    a.defaultFolded === b.defaultFolded &&
    a.badgeCount === b.badgeCount
  );
}

function dropdownItemsEqual(
  a: AppBarNavDropdownItem,
  b: AppBarNavDropdownItem,
): boolean {
  return (
    a.label === b.label &&
    a.href === b.href &&
    a.disabled === b.disabled &&
    a.dimmed === b.dimmed &&
    a.tooltip === b.tooltip &&
    a.unseen === b.unseen &&
    a.badgeCount === b.badgeCount
  );
}

/**
 * Serializable content signature for one nav item — every field that affects
 * measurement or rendering, no functions. Two navLinks arrays with equal
 * signatures need no re-measure and no re-pack (SP-2649).
 */
export function navItemSignature(item: AppBarNavItem): unknown {
  if (item.kind === "dropdown") {
    return [
      "d",
      item.label,
      item.activePath,
      item.unseen,
      item.defaultFolded,
      item.items.map((child) => [
        child.label,
        child.href,
        child.disabled,
        child.dimmed,
        child.tooltip,
        child.unseen,
        child.badgeCount,
      ]),
    ];
  }

  return [
    "l",
    item.label,
    item.href,
    item.activePath,
    item.disabled,
    item.dimmed,
    item.tooltip,
    item.unseen,
    item.defaultFolded,
    item.badgeCount,
  ];
}

export function packNavItems({
  containerW,
  navLinks,
  widths,
  activeKey,
  moreBtnW,
}: {
  containerW: number;
  navLinks: AppBarNavItem[];
  widths: Map<string, number>;
  activeKey: string | null;
  moreBtnW: number;
}): PackResult {
  if (navLinks.length === 0) {
    return { inline: [], folded: [], minInlineWidth: 0 };
  }

  const keyed = navLinks.map((item) => {
    const key = navItemKey(item);
    return { item, key, width: widths.get(key) ?? 0 };
  });

  // Role-folded items start in More; the active one is exempt so the tab for
  // the page you're on is always visible inline.
  const seededKeys = new Set(
    keyed
      .filter((k) => k.item.defaultFolded && k.key !== activeKey)
      .map((k) => k.key),
  );
  const pinned = keyed.filter((k) => !seededKeys.has(k.key));

  const pinnedSumW = pinned.reduce((a, b) => a + b.width, 0);
  const pinnedGaps = Math.max(0, pinned.length - 1) * NAV_GAP_PX;
  // Desired inline width: pinned items, plus the More button (and its gap)
  // when seeding guarantees it renders.
  const navW =
    pinnedSumW +
    pinnedGaps +
    (seededKeys.size > 0 ? moreBtnW + (pinned.length > 0 ? NAV_GAP_PX : 0) : 0);

  if (navW <= containerW) {
    return {
      inline: pinned.map((k) => k.item),
      folded: keyed.filter((k) => seededKeys.has(k.key)).map((k) => k.item),
      minInlineWidth: 0,
    };
  }

  // Reserve the More button width + the gap-3 between it and the last
  // inline item. Without the gap reservation, an item that exactly fills
  // `containerW - moreBtnW` would push More past `overflow-hidden`.
  let budget = containerW - moreBtnW - NAV_GAP_PX;
  const inlineKeys = new Set<string>();
  const activeEntry = pinned.find((k) => k.key === activeKey);

  if (activeEntry) {
    inlineKeys.add(activeEntry.key);
    budget -= activeEntry.width;
  }

  // Walk declared order over the pinned items. Stop at the first non-fitting
  // item so the inline set is a contiguous prefix (with the active item
  // reserved separately). Seeded items never re-enter inline under pressure.
  for (const k of pinned) {
    if (k.key === activeKey) {
      continue;
    }

    const cost = k.width + (inlineKeys.size > 0 ? NAV_GAP_PX : 0);

    if (cost > budget) {
      break;
    }

    inlineKeys.add(k.key);
    budget -= cost;
  }

  const activeWidth = activeEntry?.width ?? 0;
  return {
    inline: keyed.filter((k) => inlineKeys.has(k.key)).map((k) => k.item),
    folded: keyed.filter((k) => !inlineKeys.has(k.key)).map((k) => k.item),
    minInlineWidth: activeWidth + (activeWidth > 0 ? NAV_GAP_PX : 0) + moreBtnW,
  };
}
